import React, { useState, useEffect } from 'react';
import { Activity, Tv, Radio, Users, Film, RefreshCw, AlertCircle, CheckCircle, Clock, Layers, BarChart3, Zap, Stethoscope, Copy, Check } from 'lucide-react';
import * as api from '../services/api';

function Dashboard() {
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastRefresh, setLastRefresh] = useState(new Date());
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [diagnosticRunning, setDiagnosticRunning] = useState(false);
  const [diagnostic, setDiagnostic] = useState(null);
  const [diagError, setDiagError] = useState(null);
  const [copied, setCopied] = useState(false);
  const [customerUsage, setCustomerUsage] = useState([]);
  const [failoverOverview, setFailoverOverview] = useState([]);
  const [probeRunning, setProbeRunning] = useState(false);

  useEffect(() => {
    loadData();
    if (autoRefresh) {
      const interval = setInterval(() => loadData(), 30000);
      return () => clearInterval(interval);
    }
  }, [autoRefresh]);

  const loadData = async () => {
    try {
      setError(null);
      const [statsRes, usageRes, failoverRes] = await Promise.all([
        api.getStats(),
        api.getCustomerUsage().catch(() => ({ data: [] })),
        api.getAdminFailoverHealth().catch(() => ({ data: [] }))
      ]);
      setStats(statsRes.data || {});
      setCustomerUsage(Array.isArray(usageRes.data) ? usageRes.data : []);
      setFailoverOverview(Array.isArray(failoverRes.data) ? failoverRes.data : []);
      setLastRefresh(new Date());
    } catch (err) {
      console.error('Dashboard load error:', err);
      setError(err.response?.data?.error || err.message || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const formatTime = (dateStr) => {
    if (!dateStr) return 'Never';
    const date = new Date(dateStr);
    const diffMs = Date.now() - date;
    const diffMins = Math.floor(diffMs / 60000);
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return diffMins + 'm ago';
    if (diffMins < 1440) return Math.floor(diffMins / 60) + 'h ago';
    return Math.floor(diffMins / 1440) + 'd ago';
  };

  if (loading) return <div className="loading">Loading dashboard...</div>;

  return (
    <div>
      <div className="page-header">
        <div>
          <h2>Dashboard</h2>
          <p>Overview of your StreamUnity IPTV service</p>
        </div>
        <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
          <button className={`btn ${autoRefresh ? 'btn-primary' : 'btn-secondary'}`} onClick={() => setAutoRefresh(!autoRefresh)} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <RefreshCw size={16} className={autoRefresh ? 'spinning' : ''} />
            Auto-refresh {autoRefresh ? 'ON' : 'OFF'}
          </button>
          <button className="btn btn-secondary" onClick={loadData}><RefreshCw size={16} />Refresh Now</button>
        </div>
      </div>

      <div style={{ marginBottom: '10px', color: '#6b7280', fontSize: '14px' }}>
        <Clock size={14} style={{ marginRight: '4px', verticalAlign: 'middle' }} />
        Last updated: {lastRefresh.toLocaleTimeString()}
      </div>

      {error && (
        <div style={{ background: '#fee2e2', color: '#dc2626', padding: '12px 16px', borderRadius: '8px', marginBottom: '20px', fontSize: '14px' }}>
          Error loading dashboard: {error}
        </div>
      )}

      {/* Main Stats */}
      <div className="stats-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))' }}>
        <div className="stat-card"><div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}><div><h3>Customers</h3><div className="value">{customerUsage.length}</div><div className="subtext">{customerUsage.filter(c => c.enabled !== 0).length} active accounts</div></div><Users size={30} color="#0ea5e9" style={{ opacity: 0.25 }} /></div></div>
        <div className="stat-card"><div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}><div><h3>Live Channels</h3><div className="value">{(stats?.channels?.enabled || 0).toLocaleString()}</div><div className="subtext">{stats?.channels?.categories || 0} categories / {(stats?.channels?.total || 0).toLocaleString()} total</div></div><Radio size={30} color="#10b981" style={{ opacity: 0.25 }} /></div></div>
        <div className="stat-card"><div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}><div><h3>Movies (VOD)</h3><div className="value">{(stats?.vod?.enabled || 0).toLocaleString()}</div><div className="subtext">{stats?.vod?.categories || 0} categories</div></div><Film size={30} color="#f59e0b" style={{ opacity: 0.25 }} /></div></div>
        <div className="stat-card"><div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}><div><h3>Series</h3><div className="value">{(stats?.series?.enabled || 0).toLocaleString()}</div><div className="subtext">{stats?.series?.categories || 0} categories</div></div><Tv size={30} color="#8b5cf6" style={{ opacity: 0.25 }} /></div></div>
        <div className="stat-card"><div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}><div><h3>Watching Now</h3><div className="value">{stats?.users?.active || 0}</div><div className="subtext">{stats?.users?.activeStreams || 0} stream(s) / {stats?.users?.enabledAccounts || stats?.users?.total || 0} accounts</div></div><Users size={30} color="#ef4444" style={{ opacity: 0.25 }} /></div></div>
        <div className="stat-card"><div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}><div><h3>EPG Coverage</h3><div className="value">{stats?.epgCoverage || 0}%</div><div className="subtext">{stats?.epgSources?.count || 0} sources</div></div><Activity size={30} color="#06b6d4" style={{ opacity: 0.25 }} /></div></div>
      </div>

      {/* User Activity */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))', gap: '25px', marginTop: '25px' }}>
        <div className="table-container">
          <div className="table-header"><h3><Zap size={20} style={{ marginRight: '8px', verticalAlign: 'middle' }} />User Activity</h3></div>
          <div style={{ padding: '20px' }}>
            <p style={{ color: '#6b7280', fontSize: '13px', marginBottom: '15px' }}>Recent user login activity</p>
            {(stats?.users?.recentlyActive || []).length > 0 ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {(stats?.users?.recentlyActive || []).slice(0, 8).map((user, i) => (
                  <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 10px', background: '#f9fafb', borderRadius: '6px', fontSize: '13px' }}>
                    <div><strong>{user.username}</strong>{user.max_connections > 0 && <span style={{ marginLeft: '8px', fontSize: '11px', color: '#6b7280' }}>{user.max_connections} conn</span>}</div>
                    <div style={{ color: '#6b7280', fontSize: '12px' }}>{user.created_at ? 'Created: ' + formatTime(user.created_at) : <span style={{ color: '#f59e0b' }}>Never logged in</span>}</div>
                  </div>
                ))}
              </div>
            ) : <p style={{ color: '#9ca3af', textAlign: 'center' }}>No user activity yet</p>}
            <div style={{ marginTop: '15px', display: 'flex', gap: '20px', fontSize: '13px', color: '#6b7280' }}>
              <div><strong style={{ color: '#111' }}>{stats?.users?.active || 0}</strong> active</div>
              <div><strong style={{ color: '#ef4444' }}>{stats?.users?.expired || 0}</strong> expired</div>
              <div><strong style={{ color: '#111' }}>{stats?.users?.total || 0}</strong> total</div>
            </div>
          </div>
        </div>
      </div>

      {/* Custom Categories */}
      {(stats?.customCategories?.total > 0) ? (
        <div className="table-container" style={{ marginTop: '25px' }}>
          <div className="table-header"><h3><Layers size={20} style={{ marginRight: '8px', verticalAlign: 'middle' }} />Custom Categories</h3></div>
          <div style={{ padding: '20px', display: 'flex', gap: '30px', alignItems: 'center', flexWrap: 'wrap' }}>
            <div style={{ textAlign: 'center' }}><div style={{ fontSize: '28px', fontWeight: 'bold', color: '#8b5cf6' }}>{stats?.customCategories?.enabled || 0}</div><div style={{ fontSize: '13px', color: '#6b7280' }}>Categories</div></div>
            <div style={{ textAlign: 'center' }}><div style={{ fontSize: '28px', fontWeight: 'bold', color: '#0ea5e9' }}>{stats?.customCategories?.totalItems || 0}</div><div style={{ fontSize: '13px', color: '#6b7280' }}>Items</div></div>
            {(stats?.customCategories?.byType || []).map(t => (
              <div key={t.item_type} style={{ textAlign: 'center', padding: '8px 16px', background: '#f3f4f6', borderRadius: '8px' }}>
                <div style={{ fontSize: '16px', fontWeight: '600' }}>{t.c}</div>
                <div style={{ fontSize: '11px', color: '#6b7280', textTransform: 'capitalize' }}>{t.item_type}</div>
              </div>
            ))}
          </div>
        </div>
      ) : null}

      {/* Failover Health Dashboard (Issue 94) */}
      <div className="table-container" style={{ marginTop: '25px' }}>
        <div className="table-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h3><Activity size={20} style={{ marginRight: '8px', verticalAlign: 'middle' }} />Failover Health &amp; Stream Probes</h3>
          <button
            className="btn btn-secondary"
            disabled={probeRunning}
            onClick={async () => {
              setProbeRunning(true);
              try {
                await api.runStreamProbes();
                await loadData();
              } catch (err) {
                alert(err.response?.data?.error || err.message);
              } finally {
                setProbeRunning(false);
              }
            }}
            style={{ padding: '6px 14px', fontSize: '13px' }}
          >
            <RefreshCw size={14} style={{ marginRight: '6px', animation: probeRunning ? 'spin 1s linear infinite' : 'none' }} />
            {probeRunning ? 'Probing...' : 'Run Probe Now'}
          </button>
        </div>
        {failoverOverview.length === 0 ? (
          <div className="empty-state"><h3>No probe data yet</h3><p>Click Run Probe Now or wait for the scheduled probe (every 30 min)</p></div>
        ) : (
          <table>
            <thead>
              <tr>
                <th>Customer</th>
                <th>Lines</th>
                <th>Probe Success</th>
                <th>Healthy Lines</th>
                <th>Failovers (7d)</th>
                <th>Backup Ready</th>
                <th>Last Probe</th>
              </tr>
            </thead>
            <tbody>
              {failoverOverview.map((c) => (
                <tr key={c.id}>
                  <td><strong>{c.username}</strong>{c.name ? <span style={{ color: '#9ca3af' }}> — {c.name}</span> : null}</td>
                  <td>{c.provider_lines}</td>
                  <td>
                    <span className={`badge badge-${(c.overall_success_rate ?? 0) >= 80 ? 'success' : (c.overall_success_rate ?? 0) >= 50 ? 'warning' : 'danger'}`}>
                      {c.overall_success_rate != null ? `${c.overall_success_rate}%` : '—'}
                    </span>
                  </td>
                  <td>{c.healthy_lines ?? 0}/{c.lines_total ?? c.lines_probed ?? 0}</td>
                  <td>{c.failovers_7d ?? 0}</td>
                  <td><span className={`badge badge-${c.failover_ready ? 'success' : 'warning'}`}>{c.failover_ready ? 'Yes' : 'No'}</span></td>
                  <td>{formatTime(c.last_probe_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        <p style={{ padding: '12px 20px', margin: 0, fontSize: '12px', color: '#9ca3af' }}>
          Health checks use lightweight player_api pings every 30 minutes (they do not open live streams or use your connection slots). Failover still kicks in during playback if a line fails.
        </p>
      </div>


      {/* Quick Actions */}
      <div className="table-container" style={{ marginTop: '25px', marginBottom: '20px' }}>
        <div className="table-header"><h3>Quick Actions</h3></div>
        <div style={{ padding: '20px', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '12px' }}>
          <a href="/customers" className="btn btn-primary" style={{ textAlign: 'center' }}><Users size={16} />Customers</a>
          <a href="/channels" className="btn btn-secondary" style={{ textAlign: 'center' }}><Radio size={16} />Channels</a>
          <a href="/movies" className="btn btn-secondary" style={{ textAlign: 'center' }}><Film size={16} />Movies</a>
          <a href="/series" className="btn btn-secondary" style={{ textAlign: 'center' }}><Tv size={16} />Series</a>
          <a href="/users" className="btn btn-secondary" style={{ textAlign: 'center' }}><Users size={16} />Users</a>
          <a href="/custom-categories" className="btn btn-secondary" style={{ textAlign: 'center' }}><Layers size={16} />Categories</a>
        </div>
      </div>

      {/* Diagnostic & Self-Healing */}
      <div className="table-container" style={{ marginTop: '25px', marginBottom: '20px' }}>
        <div className="table-header">
          <h3><Stethoscope size={20} style={{ marginRight: '8px', verticalAlign: 'middle' }} />Diagnostic &amp; Self-Healing</h3>
        </div>
        <div style={{ padding: '20px' }}>
          <p style={{ margin: '0 0 15px 0', color: '#6b7280', fontSize: '14px' }}>
            Runs a comprehensive health check on all services (API, database, customer sync, streams, EPG, system resources) and attempts automatic repairs for any issues found. A reference number is generated — share it with AI chat support for deep-dive analysis.
          </p>

          <button
            className="btn"
            disabled={diagnosticRunning}
            onClick={async () => {
              setDiagnosticRunning(true);
              setDiagError(null);
              setDiagnostic(null);
              try {
                const res = await api.runDiagnostic();
                setDiagnostic(res.data);
              } catch (err) {
                setDiagError(err.response?.data?.error || err.message);
              } finally {
                setDiagnosticRunning(false);
              }
            }}
            style={{
              background: diagnosticRunning ? '#9ca3af' : 'linear-gradient(135deg, #059669, #10b981)',
              color: '#fff',
              border: 'none',
              padding: '12px 28px',
              borderRadius: '8px',
              fontSize: '15px',
              fontWeight: '600',
              cursor: diagnosticRunning ? 'not-allowed' : 'pointer',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px'
            }}
          >
            {diagnosticRunning ? (
              <><RefreshCw size={18} className="spinning" /> Running Diagnostic...</>
            ) : (
              <><Stethoscope size={18} /> Run Full System Diagnostic</>
            )}
          </button>

          {diagError && (
            <div style={{ marginTop: '15px', padding: '12px', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '8px', color: '#991b1b' }}>
              <AlertCircle size={16} style={{ marginRight: '6px', verticalAlign: 'middle' }} />
              {diagError}
            </div>
          )}

          {diagnostic && (
            <div style={{ marginTop: '20px' }}>
              {/* Reference Number */}
              <div style={{
                padding: '16px', background: '#f0fdf4', border: '2px solid #10b981', borderRadius: '10px',
                display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '12px'
              }}>
                <div>
                  <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>Reference Number</div>
                  <code style={{ fontSize: '22px', fontWeight: '700', color: '#059669', letterSpacing: '1px' }}>{diagnostic.ref}</code>
                  <div style={{ fontSize: '11px', color: '#9ca3af', marginTop: '4px' }}>
                    {diagnostic.timestamp} • {diagnostic.duration}
                  </div>
                </div>
                <button
                  className="btn btn-secondary"
                  onClick={() => {
                    navigator.clipboard.writeText(diagnostic.ref);
                    setCopied(true);
                    setTimeout(() => setCopied(false), 2000);
                  }}
                  style={{ display: 'flex', alignItems: 'center', gap: '6px' }}
                >
                  {copied ? <Check size={16} /> : <Copy size={16} />}
                  {copied ? 'Copied!' : 'Copy Ref'}
                </button>
              </div>

              {/* Summary */}
              <div style={{
                marginTop: '15px', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(100px, 1fr))', gap: '10px'
              }}>
                <div style={{ textAlign: 'center', padding: '12px', background: '#f0fdf4', borderRadius: '8px' }}>
                  <div style={{ fontSize: '24px', fontWeight: '700', color: '#059669' }}>{diagnostic.summary.passed}</div>
                  <div style={{ fontSize: '12px', color: '#6b7280' }}>Passed</div>
                </div>
                <div style={{ textAlign: 'center', padding: '12px', background: '#fef2f2', borderRadius: '8px' }}>
                  <div style={{ fontSize: '24px', fontWeight: '700', color: '#dc2626' }}>{diagnostic.summary.failed}</div>
                  <div style={{ fontSize: '12px', color: '#6b7280' }}>{diagnostic.summary.failed > 0 ? 'Failed' : 'Failed'}</div>
                </div>
                <div style={{ textAlign: 'center', padding: '12px', background: '#eff6ff', borderRadius: '8px' }}>
                  <div style={{ fontSize: '24px', fontWeight: '700', color: '#2563eb' }}>{diagnostic.summary.healed}</div>
                  <div style={{ fontSize: '12px', color: '#6b7280' }}>Healed</div>
                </div>
                <div style={{ textAlign: 'center', padding: '12px', background: '#fefce8', borderRadius: '8px' }}>
                  <div style={{ fontSize: '24px', fontWeight: '700', color: '#ca8a04' }}>{diagnostic.summary.warnings}</div>
                  <div style={{ fontSize: '12px', color: '#6b7280' }}>Warnings</div>
                </div>
              </div>

              {/* Detailed Results */}
              <div style={{ marginTop: '15px', maxHeight: '400px', overflow: 'auto' }}>
                <table style={{ width: '100%', fontSize: '13px' }}>
                  <thead>
                    <tr style={{ background: '#f9fafb' }}>
                      <th style={{ padding: '8px 12px', textAlign: 'left' }}>Check</th>
                      <th style={{ padding: '8px 12px', textAlign: 'left' }}>Result</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Object.entries(diagnostic.checks).map(([name, check]) => (
                      <tr key={name} style={{ borderBottom: '1px solid #e5e7eb' }}>
                        <td style={{ padding: '8px 12px', fontWeight: '500' }}>{name}</td>
                        <td style={{ padding: '8px 12px' }}>
                          <span style={{
                            display: 'inline-flex', alignItems: 'center', gap: '6px',
                            color: check.status === 'pass' ? '#059669' : check.status === 'fail' ? '#dc2626' : '#ca8a04'
                          }}>
                            {check.status === 'pass' ? <CheckCircle size={14} /> : check.status === 'fail' ? <AlertCircle size={14} /> : <AlertCircle size={14} />}
                            {check.detail}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Heals Performed */}
              {diagnostic.heals.length > 0 && (
                <div style={{ marginTop: '15px', padding: '12px', background: '#eff6ff', borderRadius: '8px' }}>
                  <strong style={{ fontSize: '14px', color: '#1e40af' }}>Self-Healing Actions Performed:</strong>
                  <ul style={{ margin: '8px 0 0 0', paddingLeft: '20px' }}>
                    {diagnostic.heals.map((h, i) => (
                      <li key={i} style={{ fontSize: '13px', marginBottom: '4px' }}>
                        <strong>{h.action}:</strong> {h.detail}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      
      {customerUsage.length > 0 && (
        <div className="table-container" style={{ marginTop: '25px' }}>
          <div className="table-header"><h3><Users size={18} style={{ verticalAlign: 'middle', marginRight: 6 }} />Customer Usage</h3></div>
          <table>
            <thead><tr><th>Customer</th><th>Xtream User</th><th>Providers</th><th>Last Login</th><th>Active Now</th><th>Max Conn</th><th>Failovers (7d)</th></tr></thead>
            <tbody>
              {customerUsage.map(c => (
                <tr key={c.id}>
                  <td><strong>{c.username}</strong>{c.name ? <span style={{ color: '#9ca3af' }}> — {c.name}</span> : null}</td>
                  <td><code>{c.xtream_username || '—'}</code></td>
                  <td>{c.provider_lines}</td>
                  <td>{c.last_login ? new Date(c.last_login).toLocaleString() : 'Never'}</td>
                  <td><span className={`badge badge-${(c.active_now?.active_ips || 0) > 0 ? 'success' : 'info'}`}>{c.active_now?.active_ips || 0} IP(s)</span></td>
                  <td>{c.max_connections || 1}</td>
                  <td>{c.failovers_7d || 0}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div style={{ padding: '15px', background: '#f9fafb', borderRadius: '8px', fontSize: '13px', color: '#6b7280' }}>
        <strong>StreamUnity v1.0</strong> • panel.streaminfinitytv.com • Auto-refresh: {autoRefresh ? 'ON' : 'OFF'}
      </div>
    </div>
  );
}

export default Dashboard;
